import type { MarketRegionResponseDto } from "Infrastructure/Api/Api";
import { useUpdateRegionTransportMutation } from "Market/Api/Mutation/updateRegionTransportMutation";
import { useRegionTransport } from "Market/Api/Query/useRegionTransport";
import {
	Cancel,
	CheckCircle,
	ContentCopy,
	FormatAlignLeft,
	LocationOn,
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

type RegionJsonEditorModalProps = {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	region: MarketRegionResponseDto;
};

export const RegionJsonEditorModal = ({
	isOpen,
	setIsOpen,
	region,
}: RegionJsonEditorModalProps) => {
	const [json, setJson] = useState("{}");
	const [isValid, setIsValid] = useState(true);
	const [copied, setCopied] = useState(false);

	const hasRegionCode = !!region.regionCode;

	const { data: transportData, isLoading: isTransportLoading } =
		useRegionTransport(region.regionCode);

	useEffect(() => {
		if (isOpen) {
			const raw = transportData ?? "{}";
			setJson(typeof raw === "string" ? raw : JSON.stringify(raw, null, 2));
			setIsValid(true);
		}
	}, [isOpen, transportData]);

	const { mutate: updateRegion, isPending } = useUpdateRegionTransportMutation(
		region.marketId,
		region.regionCode,
	);

	const handleChange = (value: string) => {
		setJson(value);
		try {
			JSON.parse(value);
			setIsValid(true);
		} catch {
			setIsValid(false);
		}
	};

	const handleFormat = () => {
		try {
			setJson(JSON.stringify(JSON.parse(json), null, 2));
		} catch {}
	};

	const handleCopy = () => {
		navigator.clipboard.writeText(json);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	const handleSave = () => {
		if (!isValid || !hasRegionCode) {
			return;
		}

		// we must parse and stringify to ensure the JSON is valid and properly formatted before sending to the server
		const parsed = JSON.parse(json);

		updateRegion(
			{ transport: JSON.stringify(parsed) },
			{
				onSuccess: () => setIsOpen(false),
			},
		);
	};

	return (
		<Dialog
			open={isOpen}
			onClose={() => setIsOpen(false)}
			maxWidth="md"
			fullWidth
			slotProps={{
				paper: { sx: { minHeight: 480 } },
			}}
		>
			<DialogTitle>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					gap={1}
				>
					<Stack direction="row" alignItems="center" gap={1}>
						<LocationOn fontSize="small" color="action" />
						<Typography variant="inherit">GeoJSON — {region.name}</Typography>
						<Tooltip title="Open geojson.io">
							<Typography
								component="a"
								href="https://geojson.io/next/"
								target="_blank"
								rel="noopener noreferrer"
								variant="caption"
							>
								https://geojson.io/next/
							</Typography>
						</Tooltip>
					</Stack>
					<Chip
						size="small"
						icon={isValid ? <CheckCircle /> : <Cancel />}
						label={isValid ? "Valid JSON" : "Invalid JSON"}
						color={isValid ? "success" : "error"}
						variant="outlined"
					/>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ pb: 1 }}>
				{!hasRegionCode && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						No region code set. Add a region code before editing transport data.
					</Alert>
				)}
				<Box
					sx={{
						border: 1,
						borderColor: "divider",
						borderRadius: 1,
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							px: 2,
							py: 0.5,
							bgcolor: "action.hover",
							borderBottom: 1,
							borderColor: "divider",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontFamily: "monospace", color: "text.secondary" }}
						>
							transport_{region.id}.json
						</Typography>
						<Stack direction="row" gap={0.5}>
							<Tooltip title={copied ? "Copied!" : "Copy"}>
								<IconButton size="small" onClick={handleCopy}>
									<ContentCopy fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Format JSON">
								<IconButton
									size="small"
									onClick={handleFormat}
									disabled={!isValid}
								>
									<FormatAlignLeft fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
					</Box>

					{isTransportLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: 200,
								bgcolor: "background.paper",
							}}
						>
							<CircularProgress size={24} />
						</Box>
					) : (
						<TextField
							multiline
							fullWidth
							disabled={!hasRegionCode}
							value={json}
							onChange={(e) => handleChange(e.target.value)}
							minRows={10}
							slotProps={{
								input: {
									sx: {
										fontFamily: "monospace",
										fontSize: 13,
										alignItems: "flex-start",
									},
								},
							}}
							sx={{
								"& fieldset": { border: "none" },
								bgcolor: "background.paper",
							}}
						/>
					)}
				</Box>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" onClick={() => setIsOpen(false)}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={!isValid || isTransportLoading || !hasRegionCode}
					loading={isPending}
					onClick={handleSave}
				>
					Save Changes
				</Button>
			</DialogActions>
		</Dialog>
	);
};
